/**
 * The Solution estimate role registry (Settings ▸ Labor Categories ▸ "Solution estimate
 * roles"). Roles reported by Solution's project estimates, mapped onto this app's Labor
 * Categories so an import can price them.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionEstimateRoleKeys } from "@/lib/query";
import type { SolutionEstimateRoleView } from "@/schemas/solution-estimate-roles";

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function useSolutionEstimateRoles() {
  return useQuery({
    queryKey: solutionEstimateRoleKeys.list(),
    queryFn: async (): Promise<SolutionEstimateRoleView[]> => {
      const data = await readJsonOrThrow<{ roles: SolutionEstimateRoleView[] }>(
        await fetch("/api/solution-estimate-roles"),
      );
      return data.roles;
    },
  });
}

export function useSetSolutionEstimateRoleMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; laborCategoryId: string | null }) => {
      const { id, ...patch } = input;
      return readJsonOrThrow<SolutionEstimateRoleView>(
        await fetch(`/api/solution-estimate-roles/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        }),
      );
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: solutionEstimateRoleKeys.all });
      toast.success(
        `${result.externalRoleName} now maps to ${result.laborCategoryName ?? "Unmapped"}`,
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Failed to update the mapping"),
  });
}
